import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { usePageAdf } from '@confluence/connectors/entry-points/usePageAdf.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { ConnectionGroup } from '../ConnectionsByLineMenu.tsx';
import { collectSmartLinks } from '../utils/collectSmartLinks.ts';
import {
	getSmartLinkProductGroupLabel,
	groupSmartLinksByProduct,
} from '../utils/groupSmartLinksByProduct.ts';
import { jsonLdToEnhancedSmartLink } from '../utils/jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

// Upper bound on how long to wait for the renderer to provide the page ADF before giving up and
// resolving to the empty state. Only reached when the renderer never sets pageAdf (a broken page);
// long enough that normal loads always win the race and never flash. CCINT-8752
const ADF_WAIT_TIMEOUT_MS = 10000;

export type UseSmartLinksResult = {
	groups: ConnectionGroup[];
	links: EnhancedSmartLink[];
	/**
	 * The content whose ADF produced `links`. Lags `contentId` during an SPA
	 * transition, because `links` is kept until the new page's ADF has been
	 * collected — so consumers that care which page the links belong to must key
	 * off this rather than the current `contentId`.
	 */
	collectedForContentId: string | undefined;
	loading: boolean;
	error: Error | null;
	refreshLinkUrl: (item: EnhancedSmartLink) => Promise<EnhancedSmartLink | undefined>;
};

const reportSmartLinkError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
	return errorObj;
};

type SmartLinkResponse = Parameters<typeof jsonLdToEnhancedSmartLink>[2];

const mergeRefreshedSmartLink = (
	link: EnhancedSmartLink,
	response: SmartLinkResponse,
): EnhancedSmartLink => {
	const refreshedLink = jsonLdToEnhancedSmartLink(link.url, link.occurrenceIndex, response);
	return {
		...refreshedLink,
		iconUrl: refreshedLink.iconUrl ?? link.iconUrl,
		product: link.product ?? refreshedLink.product,
		subproduct: link.subproduct ?? refreshedLink.subproduct,
	};
};

/**
 * Collects all smart links from the current page ADF, enriches them with
 * metadata from the SmartCard store (cache) and ORS (network fallback), then
 * groups them by product for consumption by ConnectionsByLineMenu.
 */
export function useSmartLinks(contentId?: string): UseSmartLinksResult {
	const { adf } = usePageAdf(contentId);
	const { connections, store } = useSmartLinkContext();

	const [links, setLinks] = useState<EnhancedSmartLink[]>([]);
	const [collectedForContentId, setCollectedForContentId] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const linksRef = useRef<EnhancedSmartLink[]>([]);

	// The SmartCard provider rebuilds its context value with `lodash.merge({}, ...)`, which mints a
	// new `store` object on every recompute even though the underlying Redux store is unchanged. We
	// read it through a ref and keep it out of the collection effect's deps so an ancestor re-render
	// (e.g. on scroll) does not re-fire the effect, flash the skeleton, and re-collect the links.
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	useEffect(() => {
		// The ADF isn't available until the renderer sets pageAdf, which on the client happens after
		// this loadable mounts. Treat "no ADF yet" as still-loading so the byline stays on the
		// placeholder instead of flashing to null (links=[]) before the ADF arrives. CCINT-8752
		if (!adf) {
			setLoading(true);
			// Escape hatch: if the renderer never provides an ADF (e.g. it failed to mount), don't hang
			// on the placeholder forever — after a generous wait, resolve to the empty state (the
			// pre-CCINT-8752 behaviour). The window is long enough that a normally-rendering page always
			// sets pageAdf first, so this never introduces a flash; if the ADF arrives later the effect
			// re-runs (adf is a dep) and collects for real.
			const timeoutId = setTimeout(() => setLoading(false), ADF_WAIT_TIMEOUT_MS);
			return () => clearTimeout(timeoutId);
		}

		let cancelled = false;

		setLoading(true);
		setError(null);

		collectSmartLinks(adf, storeRef.current, connections.client)
			.then((result) => {
				if (!cancelled) {
					linksRef.current = result;
					setLinks(result);
					// Label the result with the content whose ADF produced it. `contentId` is
					// deliberately NOT an effect dep: on an SPA transition it updates before the
					// new page's ADF arrives, so re-running on it would re-collect the *previous*
					// ADF and label those links with the new content — the exact mismatch this
					// is here to prevent. Reading it from the effect closure instead pairs each
					// result with the contentId that was current when its ADF was collected.
					setCollectedForContentId(contentId);
					setLoading(false);
				}
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				const errorObj = reportSmartLinkError(err);
				setError(errorObj);
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
		// `contentId` is intentionally omitted — see the comment on setCollectedForContentId above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adf, connections.client]);

	const refreshLinkUrl = useCallback(
		async (item: EnhancedSmartLink): Promise<EnhancedSmartLink | undefined> => {
			const productLabel = getSmartLinkProductGroupLabel(item);
			const productLinks: EnhancedSmartLink[] = [];
			for (const link of linksRef.current) {
				const isSameProduct = getSmartLinkProductGroupLabel(link) === productLabel;
				if (isSameProduct) {
					productLinks.push(link);
				}
			}

			const urlsToRefresh: string[] = [];
			const seenUrls = new Set<string>();
			for (const link of productLinks) {
				if (!seenUrls.has(link.url)) {
					seenUrls.add(link.url);
					urlsToRefresh.push(link.url);
				}
			}

			const refreshedDataByUrl = new Map<string, SmartLinkResponse>();
			await Promise.all(
				urlsToRefresh.map(async (url) => {
					try {
						const response = await connections.client.fetchData(url, true);
						refreshedDataByUrl.set(url, response);
					} catch (err: unknown) {
						reportSmartLinkError(err);
					}
				}),
			);

			if (refreshedDataByUrl.size === 0) {
				return undefined;
			}

			let refreshedMatchingItem: EnhancedSmartLink | undefined;
			const refreshedLinks = linksRef.current.map((link) => {
				if (getSmartLinkProductGroupLabel(link) !== productLabel) {
					return link;
				}
				const response = refreshedDataByUrl.get(link.url);
				if (!response) {
					return link;
				}
				const refreshedLink = mergeRefreshedSmartLink(link, response);
				if (link.url === item.url && link.occurrenceIndex === item.occurrenceIndex) {
					refreshedMatchingItem = refreshedLink;
				}
				return refreshedLink;
			});

			linksRef.current = refreshedLinks;
			setLinks(refreshedLinks);
			return refreshedMatchingItem;
		},
		[connections.client],
	);

	const groups = useMemo(() => groupSmartLinksByProduct(links), [links]);

	return { groups, links, collectedForContentId, loading, error, refreshLinkUrl };
}
